// UserProfile — /profile/:username  (public page, some actions require auth)
import { Activity, Award, Bot, Code2, FlaskConical, Lock, Settings, Shield, Star, User, Zap } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import MainLayout from '../components/layout/MainLayout'
import ActivityFeed from '../components/profile/ActivityFeed'
import PrivacyTab from '../components/profile/PrivacyTab'
import ProfileEditMode from '../components/profile/ProfileEditMode'
import SecurityTab from '../components/profile/SecurityTab'
import SettingsTab from '../components/profile/SettingsTabNew'
import { API_URL, apiFetch } from '../config/api'
import { useAuth } from '../contexts/AuthContext'
import UserProfileCreations from './UserProfileCreations'
import UserProfileFeed from './UserProfileFeed'
import UserProfileHero from './UserProfileHero'

interface ProfileUser {
  id: number; username: string; display_name: string; bio?: string; role?: string
  level?: number; total_xp?: number; followers_count?: number; following_count?: number
  posts_count?: number; agents_count?: number; avatar_url?: string; cover_color?: string; phone?: string
}

interface AgentItem { id: number; name: string; status: string; level: number; llm_type?: string }
interface ResearchItem { id: number; title: string; status: string; domain?: string; created_at: string }

type Tab = 'feed' | 'creations' | 'agents' | 'research' | 'about' | 'settings'

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  active:      { bg: 'rgba(16,185,129,0.12)',  color: '#10b981' },
  completed:   { bg: 'rgba(102,126,234,0.12)', color: '#667eea' },
  pending:     { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
  in_progress: { bg: 'rgba(59,130,246,0.12)',  color: '#3b82f6' },
}
function statusStyle(s: string) { return STATUS_COLORS[s] ?? { bg: 'rgba(148,163,184,0.12)', color: 'var(--text-tertiary)' } }
function formatDate(s: string) { try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return s } }

export default function UserProfile() {
  const { username, userId } = useParams<{ username?: string; userId?: string }>()
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()

  const [profile, setProfile] = useState<ProfileUser | null>(null)
  const [agents, setAgents] = useState<AgentItem[]>([])
  const [research, setResearch] = useState<ResearchItem[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<Tab>('feed')
  const [editMode, setEditMode] = useState(false)

  const target = userId ?? username ?? currentUser?.username
  const isNumericParam = username && /^\d+$/.test(username)
  const isOwn = userId
    ? String(currentUser?.id) === userId
    : isNumericParam
      ? String(currentUser?.id) === username
      : currentUser?.username === username || (!username && !!currentUser)

  const load = useCallback(() => {
    if (!target) return
    setLoading(true)
    apiFetch(`${API_URL}/api/user/profile/${target}`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => { setProfile(d?.user ?? d ?? null); setLoading(false) })
      .catch(() => setLoading(false))
  }, [target])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (!profile?.id) return
    apiFetch(`${API_URL}/api/agents/by-user/${profile.id}`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        setAgents((d?.agents ?? []).map((a: any) => ({ id: a.id, name: a.agent_name ?? a.name ?? 'Agent', status: a.user_type ?? 'active', level: a.level ?? 1, llm_type: a.expertise })))
      }).catch(() => {})
  }, [profile?.id])

  useEffect(() => {
    if (!profile?.id || activeTab !== 'research') return
    apiFetch(`${API_URL}/api/research/projects?owner_id=${profile.id}&limit=10`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => setResearch(d?.projects ?? []))
      .catch(() => {})
  }, [profile?.id, activeTab])

  if (loading) return <MainLayout><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div></MainLayout>
  if (!profile) return <MainLayout><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>User not found.</div></MainLayout>

  const ownerTabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'feed',      label: 'Feed',      icon: <Activity size={13} /> },
    { key: 'creations', label: 'Creations', icon: <Star size={13} /> },
    { key: 'agents',    label: 'Agents',    icon: <Bot size={13} /> },
    { key: 'research',  label: 'Research',  icon: <FlaskConical size={13} /> },
    { key: 'about',     label: 'About',     icon: <User size={13} /> },
    { key: 'settings',  label: 'Settings',  icon: <Settings size={13} /> },
  ]
  const visitorTabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'feed',      label: 'Feed',      icon: <Activity size={13} /> },
    { key: 'creations', label: 'Creations', icon: <Star size={13} /> },
    { key: 'agents',    label: 'Agents',    icon: <Bot size={13} /> },
    { key: 'research',  label: 'Research',  icon: <FlaskConical size={13} /> },
    { key: 'about',     label: 'About',     icon: <User size={13} /> },
  ]
  const tabs = isOwn ? ownerTabs : visitorTabs

  return (
    <MainLayout>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

        <UserProfileHero
          profile={profile}
          agentsCount={agents.length}
          isOwn={!!isOwn}
          onEditProfile={() => setEditMode(!editMode)}
          onRefresh={load}
        />

        {editMode && isOwn && (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 20 }}>
            <ProfileEditMode profile={profile} onSaved={() => { setEditMode(false); load() }} onCancel={() => setEditMode(false)} />
          </div>
        )}

        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: 4, background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 5, overflowX: 'auto' }}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ flex: '1 1 auto', padding: '8px 12px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: activeTab === tab.key ? '#667eea' : 'transparent', color: activeTab === tab.key ? '#fff' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 12, padding: 20, minHeight: 200 }}>
          {activeTab === 'feed'      && <UserProfileFeed userId={profile.id} />}
          {activeTab === 'creations' && <UserProfileCreations userId={profile.id} isOwn={!!isOwn} />}
          {activeTab === 'agents'    && <AgentsTab agents={agents} navigate={navigate} isOwn={!!isOwn} />}
          {activeTab === 'research'  && <ResearchTab research={research} navigate={navigate} />}
          {activeTab === 'about'     && <AboutTab profile={profile} agents={agents} />}
          {activeTab === 'settings'  && isOwn && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <SettingsTab />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                    <Lock size={14} color="#6366f1" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Privacy</span>
                  </div>
                  <PrivacyTab userId={profile.id} />
                </div>
                <div style={{ background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                    <Shield size={14} color="#10b981" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Security</span>
                  </div>
                  <SecurityTab userId={profile.id} />
                </div>
              </div>
            </div>
          )}
          {activeTab === 'settings' && !isOwn && <ActivityFeed userId={profile.id} />}
        </div>
      </div>
    </MainLayout>
  )
}

// --- Sub-tab components ---

function AgentsTab({ agents, navigate, isOwn }: { agents: AgentItem[]; navigate: (p: string) => void; isOwn: boolean }) {
  if (agents.length === 0) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <Bot size={32} style={{ color: 'var(--text-tertiary)', marginBottom: 10 }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>No agents yet</div>
      {isOwn && <button onClick={() => navigate('/agents/new')} style={{ padding: '7px 16px', borderRadius: 8, background: '#667eea', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Create Agent</button>}
    </div>
  )
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {agents.map(a => (
        <div key={a.id} onClick={() => navigate(`/agent/${a.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, background: 'var(--body-bg)', border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'border-color 0.15s' }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = '#667eea')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-color)')}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{a.name?.[0]?.toUpperCase()}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{a.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{a.llm_type ?? 'AI'} · Level {a.level}</div>
          </div>
          <div style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 600, ...statusStyle(a.status) }}>{a.status}</div>
        </div>
      ))}
    </div>
  )
}

function ResearchTab({ research, navigate }: { research: ResearchItem[]; navigate: (p: string) => void }) {
  if (research.length === 0) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <FlaskConical size={32} style={{ color: 'var(--text-tertiary)', marginBottom: 10 }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>No research projects</div>
    </div>
  )
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {research.map(r => (
        <div key={r.id} onClick={() => navigate(`/research/${r.id}`)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 10, background: 'var(--body-bg)', border: '1px solid var(--border-color)', cursor: 'pointer', transition: 'border-color 0.15s' }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = '#667eea')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--border-color)')}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{r.title}</div>
            {r.domain && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.domain} · {formatDate(r.created_at)}</div>}
          </div>
          <div style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 600, ...statusStyle(r.status) }}>{r.status}</div>
        </div>
      ))}
    </div>
  )
}

function AboutTab({ profile, agents }: { profile: ProfileUser; agents: AgentItem[] }) {
  const skills = [...new Set(agents.map(a => a.llm_type).filter(Boolean))]
  const badges: { icon: React.ReactNode; label: string; color: string }[] = []
  if ((profile.level ?? 0) >= 10) badges.push({ icon: <Zap size={11} />, label: 'Power User', color: '#f59e0b' })
  if ((profile.posts_count ?? 0) >= 10) badges.push({ icon: <Activity size={11} />, label: 'Active Poster', color: '#6366f1' })
  if (agents.length >= 3) badges.push({ icon: <Bot size={11} />, label: 'Agent Master', color: '#ec4899' })
  if ((profile.total_xp ?? 0) >= 5000) badges.push({ icon: <Star size={11} />, label: 'XP Legend', color: '#10b981' })
  if (profile.role === 'admin') badges.push({ icon: <Award size={11} />, label: 'Admin', color: '#ef4444' })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {profile.bio && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Bio</div>
          <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.7, margin: 0 }}>{profile.bio}</p>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Code2 size={13} color="#6366f1" />
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>Skills</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {skills.length > 0
              ? skills.slice(0, 10).map(s => <span key={s} style={{ padding: '3px 9px', borderRadius: 6, background: 'rgba(99,102,241,0.1)', color: '#6366f1', fontSize: 11, fontWeight: 600 }}>{s}</span>)
              : <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No skills yet</span>}
          </div>
        </div>
        <div style={{ background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Award size={13} color="#f59e0b" />
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>Badges</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {badges.length > 0
              ? badges.map(b => (
                <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 6, background: `${b.color}18`, fontSize: 11, fontWeight: 600, color: b.color }}>
                  {b.icon}{b.label}
                </div>
              ))
              : <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Keep going to earn badges!</span>}
          </div>
        </div>
      </div>
    </div>
  )
}
